import React, { useState, useEffect } from 'react';

const VisitorsManagement = () => {
  const [visitors, setVisitors] = useState([]);
  const [filteredVisitors, setFilteredVisitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chatHistory, setChatHistory] = useState([]);
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(9);
  
  const [filterMonth, setFilterMonth] = useState('');
  const [filterYear, setFilterYear] = useState(new Date().getFullYear().toString());

  const API_URL = process.env.REACT_APP_API_URL || 'https://chatpanel.my.id/api';

  useEffect(() => {
    fetchVisitors();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [visitors, filterMonth, filterYear, searchQuery]);

  const fetchVisitors = async () => {
    try {
      const response = await fetch(`${API_URL}/visitors`);
      const data = await response.json();
      setVisitors(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching visitors:', error);
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...visitors];

    if (searchQuery) {
      filtered = filtered.filter(visitor => 
        visitor.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        visitor.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        visitor.session_id?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (filterYear) {
      filtered = filtered.filter(visitor => {
        const visitorYear = new Date(visitor.last_active).getFullYear().toString();
        return visitorYear === filterYear;
      });
    }

    if (filterMonth) {
      filtered = filtered.filter(visitor => {
        const visitorMonth = (new Date(visitor.last_active).getMonth() + 1).toString().padStart(2, '0');
        return visitorMonth === filterMonth;
      });
    }

    setFilteredVisitors(filtered);
    setCurrentPage(1);
  };

  const viewChat = async (sessionId, visitorName) => {
    try {
      const response = await fetch(`${API_URL}/visitors/${sessionId}/messages`);
      const data = await response.json();
      
      const messages = data.data || data || [];
      
      console.log('Chat history loaded:', messages.length, 'messages');
      setChatHistory(messages);
      setSelectedVisitor({ id: sessionId, name: visitorName });
      setShowModal(true);
    } catch (error) {
      console.error('Error fetching chat history:', error);
      setChatHistory([]);
      alert('Failed to load chat history. Please try again.');
    }
  };

  const exportVisitorsCSV = () => {
    const headers = ['Name', 'Email', 'Session ID', 'Conversations', 'Messages', 'Last Active'];
    const csvContent = [
      headers.join(','),
      ...filteredVisitors.map(v => [
        v.name || 'N/A',
        v.email,
        v.session_id,
        v.conversation_count || 0,
        v.message_count || 0,
        new Date(v.last_active).toLocaleString()
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `visitors_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const totalVisitors = filteredVisitors.length;
  const totalConversations = filteredVisitors.reduce((sum, v) => sum + (v.conversation_count || 0), 0);
  const totalMessages = filteredVisitors.reduce((sum, v) => sum + (v.message_count || 0), 0);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentVisitors = filteredVisitors.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredVisitors.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const months = [
    { value: '', label: 'All Months' },
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', color: '#666' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '24px', marginBottom: '12px' }}>👥</div>
          <p>Loading visitors...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in-up">
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontSize: '36px', fontWeight: '700', marginBottom: '8px', color: '#1a1a1a' }}>
          👥 Visitors Management
        </h1>
        <p style={{ fontSize: '16px', color: '#666666', margin: 0 }}>
          Track visitor engagement and chat history.
        </p>
      </div>

      <div className="glass-card" style={{ marginBottom: '24px', padding: '16px', display: 'flex', justifyContent: 'flex-end' }}>
        <button onClick={exportVisitorsCSV} className="glass-button-primary">
          <span style={{ marginRight: '8px' }}>📊</span>
          Export Visitors CSV
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px', marginBottom: '32px' }}>
        <div className="stats-card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <h3 style={{ fontSize: '14px', color: 'rgba(26,26,26,0.7)', margin: 0, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Visitors</h3>
            <span style={{ fontSize: '24px' }}>👤</span>
          </div>
          <p style={{ fontSize: '42px', fontWeight: '700', color: '#700000', margin: '8px 0' }}>{totalVisitors}</p>
          <p style={{ fontSize: '13px', color: 'rgba(26,26,26,0.6)', margin: 0 }}>Unique visitors tracked</p>
        </div>

        <div className="stats-card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <h3 style={{ fontSize: '14px', color: 'rgba(26,26,26,0.7)', margin: 0, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Conversations</h3>
            <span style={{ fontSize: '24px' }}>💬</span>
          </div>
          <p style={{ fontSize: '42px', fontWeight: '700', color: '#a30021', margin: '8px 0' }}>{totalConversations}</p>
          <p style={{ fontSize: '13px', color: 'rgba(26,26,26,0.6)', margin: 0 }}>Total chat sessions</p>
        </div>

        <div className="stats-card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <h3 style={{ fontSize: '14px', color: 'rgba(26,26,26,0.7)', margin: 0, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Messages</h3>
            <span style={{ fontSize: '24px' }}>📨</span>
          </div>
          <p style={{ fontSize: '42px', fontWeight: '700', color: '#cc0033', margin: '8px 0' }}>{totalMessages}</p>
          <p style={{ fontSize: '13px', color: 'rgba(26,26,26,0.6)', margin: 0 }}>Messages exchanged</p>
        </div>
      </div>

      <div className="glass-card" style={{ marginBottom: '24px', padding: '20px' }}>
        <div style={{ marginBottom: '16px' }}>
          <input
            type="text"
            placeholder="🔍 Search by name, email, or session ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="glass-input"
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <label style={{ fontSize: '14px', fontWeight: '600', color: '#1a1a1a' }}>Year:</label>
          <select value={filterYear} onChange={(e) => setFilterYear(e.target.value)} style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', color: '#1a1a1a', background: 'white' }}>
            {years.map(year => (<option key={year} value={year}>{year}</option>))}
          </select>

          <label style={{ fontSize: '14px', fontWeight: '600', color: '#1a1a1a' }}>Month:</label>
          <select value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)} style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', color: '#1a1a1a', background: 'white' }}>
            {months.map(month => (<option key={month.value} value={month.value}>{month.label}</option>))}
          </select>

          <div style={{ marginLeft: 'auto', fontSize: '14px', color: '#666', fontWeight: '500' }}>
            Showing {currentVisitors.length} of {filteredVisitors.length} visitors
          </div>
        </div>
      </div>

      {currentVisitors.length === 0 ? (
        <div className="glass-card" style={{ padding: '60px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
          <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#1a1a1a', marginBottom: '8px' }}>No visitors found</h3>
          <p style={{ fontSize: '14px', color: '#666' }}>Try adjusting your search or filters</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px', marginBottom: '32px' }}>
          {currentVisitors.map((visitor, index) => (
            <div key={index} className="glass-card" style={{ padding: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid rgba(26,26,26,0.1)' }}>
                <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'linear-gradient(135deg, #700000, #a30021)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', fontWeight: '700', color: '#ffffff' }}>
                  {visitor.name ? visitor.name.charAt(0).toUpperCase() : '?'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '16px', fontWeight: '600', color: '#1a1a1a', marginBottom: '4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {visitor.name || 'Anonymous'}
                  </div>
                  <div style={{ fontSize: '13px', color: '#666', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {visitor.email}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                <div style={{ flex: 1, background: 'rgba(255,255,255,0.3)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', padding: '12px' }}>
                  <div style={{ fontSize: '12px', color: 'rgba(26,26,26,0.6)', marginBottom: '4px' }}>💬 Conversations</div>
                  <div style={{ fontSize: '20px', fontWeight: '700', color: '#1a1a1a' }}>{visitor.conversation_count || 0}</div>
                </div>
                <div style={{ flex: 1, background: 'rgba(255,255,255,0.3)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', padding: '12px' }}>
                  <div style={{ fontSize: '12px', color: 'rgba(26,26,26,0.6)', marginBottom: '4px' }}>📨 Messages</div>
                  <div style={{ fontSize: '20px', fontWeight: '700', color: '#1a1a1a' }}>{visitor.message_count || 0}</div>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: '13px', color: '#666' }}>🕒 {new Date(visitor.last_active).toLocaleDateString()}</div>
                <button onClick={() => viewChat(visitor.session_id, visitor.name)} className="glass-button-primary" style={{ fontSize: '13px', padding: '8px 16px' }}>
                  View Chat →
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
          <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1} className="glass-button" style={{ opacity: currentPage === 1 ? 0.5 : 1 }}>← Previous</button>
          {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
            let pageNum;
            if (totalPages <= 5) pageNum = i + 1;
            else if (currentPage <= 3) pageNum = i + 1;
            else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
            else pageNum = currentPage - 2 + i;
            return (<button key={pageNum} onClick={() => paginate(pageNum)} className={currentPage === pageNum ? 'glass-button-primary' : 'glass-button'} style={{ minWidth: '40px' }}>{pageNum}</button>);
          })}
          <button onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages} className="glass-button" style={{ opacity: currentPage === totalPages ? 0.5 : 1 }}>Next →</button>
        </div>
      )}

      {showModal && (
        <div onClick={() => setShowModal(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '20px' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '16px', width: '100%', maxWidth: '800px', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '24px', borderBottom: '1px solid #e5e7eb' }}>
              <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#1a1a1a', margin: 0 }}>💬 Chat History - {selectedVisitor?.name || 'Anonymous'}</h3>
              <button onClick={() => setShowModal(false)} style={{ background: '#f3f4f6', border: 'none', borderRadius: '8px', padding: '8px 16px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', color: '#374151' }}>Close</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '24px', background: '#f9fafb' }}>
              {chatHistory.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
                  <div style={{ fontSize: '48px', marginBottom: '12px' }}>💬</div>
                  <p style={{ fontSize: '16px' }}>No messages yet</p>
                </div>
              ) : (
                chatHistory.map((msg, index) => (
                  <div key={index} style={{ marginBottom: '16px', display: 'flex', justifyContent: msg.sender_type === 'user' ? 'flex-end' : 'flex-start' }}>
                    <div style={{ maxWidth: '70%', padding: '12px 16px', borderRadius: '12px', background: msg.sender_type === 'user' ? '#700000' : 'white', color: msg.sender_type === 'user' ? 'white' : '#1a1a1a', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                        <strong style={{ fontSize: '13px', fontWeight: '600' }}>{msg.sender_type === 'user' ? '👤 ' + (selectedVisitor?.name || 'User') : '🤖 Bot'}</strong>
                        <span style={{ fontSize: '11px', opacity: 0.7 }}>{new Date(msg.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                      <div style={{ fontSize: '14px', lineHeight: '1.5' }}>{msg.content}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VisitorsManagement;
